import React, { createContext, useContext, useLayoutEffect, useRef, useState } from 'react';

export interface GapOptions {
  squareSide: number; // px, side of the largest square
  iconSize: number; // px, building icons
  iconRows: number; // icons stack this many high in every region
  radius: number; // corner radius as a fraction of a square's side
  chips: boolean; // small flag beside each region name
  multiples: boolean; // "N× smaller" labels
  unicorns: 'icons' | 'squares';
  unicornSet: 'all' | 'ai'; // all sectors (published) or AI only (our count)
  maxWidth: number; // rem, width of the whole figure
}

export const defaultGapOptions: GapOptions = {
  squareSide: 170,
  iconSize: 12,
  iconRows: 8,
  radius: 0.12,
  chips: true,
  multiples: false,
  unicorns: 'icons',
  unicornSet: 'ai',
  maxWidth: 60,
};

export const GapOptionsContext = createContext<GapOptions>(defaultGapOptions);

type RegionId = 'us' | 'cn' | 'eu';
interface Region {
  id: RegionId;
  name: string;
  value: number;
  display: string;
}
interface Metric {
  key: string;
  label: string;
  count?: boolean; // a count of companies: can be shown as one icon each
  regions: Region[];
}

const METRICS: Metric[] = [
  {
    key: 'capacity',
    label: 'Largest AI data centres, power',
    regions: [
      { id: 'us', name: 'United States', value: 11916, display: '11.9 GW' },
      { id: 'cn', name: 'China', value: 632, display: '632 MW' },
      { id: 'eu', name: 'Europe', value: 163, display: '163 MW' },
    ],
  },
  {
    key: 'unicorns',
    label: 'New unicorns, first half of 2026',
    count: true,
    regions: [
      { id: 'us', name: 'United States', value: 110, display: '110' },
      { id: 'cn', name: 'China', value: 38, display: '38' },
      { id: 'eu', name: 'Europe', value: 27, display: '27' },
    ],
  },
];

// AI-only counts are our own tally of Crunchbase's monthly Unicorn Board recaps (strict: AI labs, AI infrastructure, robotics, AI chips).
const UNICORN_SETS: Record<'all' | 'ai', { label: string; values: number[] }> = {
  all: { label: 'New unicorns, first half of 2026', values: [110, 38, 27] },
  ai: { label: 'New AI unicorns, first half of 2026', values: [44, 23, 6] },
};

const metricsFor = (o: GapOptions): Metric[] =>
  METRICS.map((m) =>
    m.key === 'unicorns'
      ? {
          ...m,
          label: UNICORN_SETS[o.unicornSet].label,
          regions: m.regions.map((r, i) => ({ ...r, value: UNICORN_SETS[o.unicornSet].values[i], display: String(UNICORN_SETS[o.unicornSet].values[i]) })),
        }
      : m
  );

const defaultCaption = (o: GapOptions) =>
  o.unicornSet === 'ai'
    ? "Power: only the largest AI data centres Epoch tracks (in Europe: UK, Norway, Portugal), so smaller European sites aren't counted. Unicorns: companies that joined Crunchbase's Unicorn Board in H1 2026. AI unicorns (labs, infrastructure, robotics, chips) are our own count from its monthly recaps."
    : "Power: only the largest AI data centres Epoch tracks (in Europe: UK, Norway, Portugal), so smaller European sites aren't counted. Unicorns: companies that joined Crunchbase's Unicorn Board in H1 2026, across all sectors but led by AI and robotics.";

const font: React.CSSProperties = { fontFamily: 'Poppins, sans-serif' };
const REGION_COLOR: Record<RegionId, string> = {
  us: 'var(--color-silver)',
  cn: 'var(--color-deep-red)',
  eu: 'var(--color-alt-accent)',
};

const star = (cx: number, cy: number, R: number) => {
  const pts: string[] = [];
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? R : R * 0.382;
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    pts.push(`${(cx + r * Math.cos(a)).toFixed(2)},${(cy + r * Math.sin(a)).toFixed(2)}`);
  }
  return pts.join(' ');
};

// Simplified flags on a square canvas, used only as small chips.
const FlagArt: React.FC<{ id: RegionId }> = ({ id }) => {
  if (id === 'us') {
    return (
      <>
        {Array.from({ length: 13 }, (_, i) => (
          <rect key={i} x={0} y={(i * 100) / 13} width={100} height={100 / 13 + 0.2} fill={i % 2 === 0 ? '#B22234' : '#FFFFFF'} />
        ))}
        <rect x={0} y={0} width={46} height={(100 / 13) * 7} fill="#3C3B6E" />
      </>
    );
  }
  if (id === 'cn') {
    return (
      <>
        <rect width={100} height={100} fill="#DE2910" />
        <polygon points={star(24, 26, 15)} fill="#FFDE00" />
      </>
    );
  }
  return (
    <>
      <rect width={100} height={100} fill="#003399" />
      {Array.from({ length: 12 }, (_, i) => {
        const a = (i * Math.PI) / 6;
        return <polygon key={i} points={star(50 + 32 * Math.sin(a), 50 - 32 * Math.cos(a), 7)} fill="#FFCC00" />;
      })}
    </>
  );
};

const FlagChip: React.FC<{ id: RegionId }> = ({ id }) => (
  <svg width={16} height={16} viewBox="0 0 100 100" style={{ borderRadius: 3, flex: 'none' }} aria-hidden="true">
    <FlagArt id={id} />
  </svg>
);

const BuildingIcon: React.FC<{ color: string; size: number }> = ({ color, size }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" aria-hidden="true">
    <rect x="2" y="1" width="10" height="12" rx="1.6" fill={color} />
    {[0, 1, 2].map((r) =>
      [0, 1].map((c) => <rect key={`${r}-${c}`} x={4 + c * 3.6} y={3 + r * 2.6} width="2" height="1.6" rx="0.4" fill="var(--color-bg-primary)" />)
    )}
    <rect x="5.9" y="10.2" width="2.2" height="2.8" rx="0.4" fill="var(--color-bg-primary)" />
  </svg>
);

const timesUs = (m: Metric, r: Region) => Math.round(m.regions[0].value / r.value);

const RegionLabel: React.FC<{ m: Metric; r: Region; o: GapOptions }> = ({ m, r, o }) => (
  <div style={{ ...font, minWidth: 0 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9rem', color: 'var(--color-text-primary)' }}>
      {o.chips && <FlagChip id={r.id} />}
      <strong>{r.name}</strong>
    </div>
    <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginTop: 2 }}>
      {r.display}
      {o.multiples && r.id !== 'us' && <> · {timesUs(m, r)}× smaller</>}
    </div>
  </div>
);

// Everything is sized from the width this panel actually has, so it never pushes past the viewport.
const Panel: React.FC<{ m: Metric; o: GapOptions; pw: number }> = ({ m, o: baseOptions, pw }) => {
  const tiny = pw < 300;
  const compact = pw < 400;
  const o: GapOptions = { ...baseOptions, chips: baseOptions.chips && !tiny };
  const asIcons = m.count && o.unicorns === 'icons';
  const n = m.regions.length;
  const COL_GAP = tiny ? 10 : compact ? 14 : 20;

  const smallCol = tiny ? 62 : compact ? 76 : 100;
  const squareSide = Math.max(60, Math.min(o.squareSide, pw - (n - 1) * (smallCol + COL_GAP)));
  const side = (r: Region) => Math.max(squareSide * Math.sqrt(r.value / m.regions[0].value), 4);
  const colWidth = (r: Region) => (r.id === 'us' ? side(r) : smallCol);

  const step = o.iconSize + 3;
  const minLabel = tiny ? 62 : compact ? 78 : 84;
  const iconWidths = (rows: number) => m.regions.map((r) => Math.max(minLabel, Math.ceil(r.value / rows) * step - 3));
  let rows = o.iconRows;
  while (rows < 40 && iconWidths(rows).reduce((x, y) => x + y, 0) + (n - 1) * COL_GAP > pw) rows++;
  const iconCols = iconWidths(rows);

  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ ...font, fontSize: '0.95rem', fontWeight: 500, color: 'var(--color-text-primary)', marginBottom: '0.9rem' }}>{m.label}</div>
      {asIcons ? (
        <div style={{ display: 'flex', gap: COL_GAP, alignItems: 'flex-start' }}>
          {m.regions.map((r, i) => (
            <div key={r.id} style={{ flex: 'none', width: iconCols[i] }}>
              <RegionLabel m={m} r={r} o={o} />
              <div
                style={{
                  display: 'grid',
                  gridAutoFlow: 'column',
                  gridTemplateRows: `repeat(${rows}, ${o.iconSize}px)`,
                  gridAutoColumns: `${o.iconSize}px`,
                  gap: 3,
                  marginTop: '0.5rem',
                }}
              >
                {Array.from({ length: r.value }, (_, k) => (
                  <BuildingIcon key={k} color={REGION_COLOR[r.id]} size={o.iconSize} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: COL_GAP, alignItems: 'flex-end', height: squareSide }}>
            {m.regions.map((r) => (
              <div key={r.id} style={{ flex: 'none', width: colWidth(r) }}>
                <div
                  style={{
                    width: side(r),
                    height: side(r),
                    background: REGION_COLOR[r.id],
                    borderRadius: Math.min(Math.max(side(r) * o.radius, 2), 24),
                  }}
                />
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: COL_GAP, alignItems: 'flex-start', marginTop: '0.6rem' }}>
            {m.regions.map((r) => (
              <div key={r.id} style={{ flex: 'none', width: colWidth(r) }}>
                <RegionLabel m={m} r={r} o={o} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export interface GapSource {
  label: string;
  url: string;
}

interface EuropeGapVisualProps {
  caption?: string;
  sources?: GapSource[];
}

const EuropeGapVisual: React.FC<EuropeGapVisualProps> = ({ caption, sources }) => {
  const o = useContext(GapOptionsContext);
  const cap = caption ?? defaultCaption(o);
  const ref = useRef<HTMLElement>(null);
  const [measured, setMeasured] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setMeasured(el.clientWidth);
    update();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(el);
    window.addEventListener('resize', update);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);
  const width = measured || 600;
  const cols = width >= 700 ? 2 : 1;
  const panelWidth = cols === 2 ? (width - 40) / 2 : width;
  return (
    <figure ref={ref} className="mx-auto mb-6 w-full" style={{ maxWidth: `min(100%, ${o.maxWidth}rem)`, minWidth: 0 }}>
      <div data-gap-export="">
      <div style={{ ...font, fontSize: '1.1rem', fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: '1.25rem' }}>
        Europe is far behind on big AI data centres and on AI startups that scale
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: '2rem 2.5rem' }}>
        {metricsFor(o).map((m) => (
          <Panel key={m.key} m={m} o={o} pw={panelWidth} />
        ))}
      </div>
      </div>
      {(cap || sources) && (
        <figcaption className="text-sm text-center px-2" style={{ ...font, marginTop: '1.75rem' }}>
          {cap && <span className="block text-[var(--color-text-secondary)] mb-1">{cap}</span>}
          {sources && (
            <span className="block text-[var(--color-text-muted)] text-xs">
              Sources:{' '}
              {sources.map((s, i) => (
                <React.Fragment key={s.url}>
                  {i > 0 && ' · '}
                  <a href={s.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline', textUnderlineOffset: 2 }}>
                    {s.label}
                  </a>
                </React.Fragment>
              ))}
            </span>
          )}
        </figcaption>
      )}
    </figure>
  );
};

export default EuropeGapVisual;
